import type { MenuItem, NavbarAuth, NavbarLogo } from '@/components/navbar'
import type { Locale } from '@/i18n/locales'

/**
 * The site's shared navigation, built per locale for the global shell (`AppLayout`).
 *
 * Two kinds of content meet here and are deliberately kept apart:
 *
 *   - **Copy** — section titles, the c2c link templates, account labels — lives in the
 *     translation store (`src/content/translations/ui/navigation.json`) and arrives
 *     through the injected translator, so translators own every visible string.
 *   - **Structure** — which routes each dropdown promotes, and the paths they point at
 *     — is editorial routing data, not translatable prose, so it stays here. It is
 *     keyed by locale because the promoted set genuinely differs: es-mx leads with a
 *     Mexico route and lists Mexican carriers (mirrors ui-react's `carrierData.js`).
 *
 * City names sit with the structure rather than the store for the same reason the
 * store holds the *editorial* rendering of a place: these are a hand-picked list, and
 * a route's label is its two cities plugged into the mode template.
 */

/** Minimal translator shape — keeps this module React-free and unit-testable. */
export type Translate = (key: string, values?: Record<string, string>) => string

/** A promoted c2c route: the two city names as displayed, plus its locale-less path. */
interface RouteLink {
    from: string
    to: string
    path: string
}

/** A promoted carrier: its name is a proper noun, so it is never translated. */
interface CarrierLink {
    name: string
    path: string
}

const BUS_ROUTES: Record<Locale, RouteLink[]> = {
    'en-us': [
        { from: 'Boston', to: 'NYC', path: '/bus/us-ma/boston/us-ny/new-york/' },
        { from: 'NYC', to: 'Boston', path: '/bus/us-ny/new-york/us-ma/boston/' },
        { from: 'DC', to: 'NYC', path: '/bus/us-dc/washington/us-ny/new-york/' },
        { from: 'LA', to: 'Vegas', path: '/bus/us-ca/los-angeles/us-nv/las-vegas/' },
        { from: 'Dallas', to: 'Houston', path: '/bus/us-tx/dallas/us-tx/houston/' },
        { from: 'Houston', to: 'Dallas', path: '/bus/us-tx/houston/us-tx/dallas/' },
        { from: 'Baltimore', to: 'NYC', path: '/bus/us-md/baltimore/us-ny/new-york/' },
        { from: 'NYC', to: 'Albany', path: '/bus/us-ny/new-york/us-ny/albany/' },
        { from: 'Orlando', to: 'Miami', path: '/bus/us-fl/orlando/us-fl/miami/' },
        { from: 'Philadelphia', to: 'NYC', path: '/bus/us-pa/philadelphia/us-ny/new-york/' },
    ],
    'es-mx': [
        { from: 'Mexico DF', to: 'Tijuana', path: '/bus/mx-cmx/mexico-city/mx-bcn/tijuana/' },
        { from: 'Nueva York', to: 'Boston', path: '/bus/us-ny/new-york/us-ma/boston/' },
        { from: 'Washington DC', to: 'Nueva York', path: '/bus/us-dc/washington/us-ny/new-york/' },
        { from: 'Nueva York', to: 'Washington DC', path: '/bus/us-ny/new-york/us-dc/washington/' },
        { from: 'Filadelfia', to: 'Nueva York', path: '/bus/us-pa/philadelphia/us-ny/new-york/' },
        { from: 'Nueva York', to: 'Filadelfia', path: '/bus/us-ny/new-york/us-pa/philadelphia/' },
        { from: 'Baltimore', to: 'Nueva York', path: '/bus/us-md/baltimore/us-ny/new-york/' },
        { from: 'Nueva York', to: 'Baltimore', path: '/bus/us-ny/new-york/us-md/baltimore/' },
    ],
}

const TRAIN_ROUTES: Record<Locale, RouteLink[]> = {
    'en-us': [
        { from: 'Boston', to: 'NYC', path: '/train/us-ma/boston/us-ny/new-york/' },
        { from: 'NYC', to: 'Boston', path: '/train/us-ny/new-york/us-ma/boston/' },
        { from: 'DC', to: 'NYC', path: '/train/us-dc/washington/us-ny/new-york/' },
        { from: 'NYC', to: 'DC', path: '/train/us-ny/new-york/us-dc/washington/' },
        { from: 'San Diego', to: 'LA', path: '/train/us-ca/san-diego/us-ca/los-angeles/' },
        { from: 'LA', to: 'San Diego', path: '/train/us-ca/los-angeles/us-ca/san-diego/' },
        { from: 'Seattle', to: 'Vancouver', path: '/train/us-wa/seattle/ca-bc/vancouver/' },
        { from: 'Vancouver', to: 'Seattle', path: '/train/ca-bc/vancouver/us-wa/seattle/' },
        { from: 'Philadelphia', to: 'NYC', path: '/train/us-pa/philadelphia/us-ny/new-york/' },
        { from: 'NYC', to: 'Baltimore', path: '/train/us-ny/new-york/us-md/baltimore/' },
        { from: 'Newark', to: 'DC', path: '/train/us-nj/newark/us-dc/washington/' },
    ],
    'es-mx': [
        { from: 'Boston', to: 'Nueva York', path: '/train/us-ma/boston/us-ny/new-york/' },
        { from: 'Nueva York', to: 'Boston', path: '/train/us-ny/new-york/us-ma/boston/' },
        {
            from: 'Washington DC',
            to: 'Nueva York',
            path: '/train/us-dc/washington/us-ny/new-york/',
        },
        {
            from: 'Nueva York',
            to: 'Washington DC',
            path: '/train/us-ny/new-york/us-dc/washington/',
        },
        { from: 'San Diego', to: 'Los Angeles', path: '/train/us-ca/san-diego/us-ca/los-angeles/' },
        { from: 'Los Angeles', to: 'San Diego', path: '/train/us-ca/los-angeles/us-ca/san-diego/' },
        { from: 'Seattle', to: 'Vancouver', path: '/train/us-wa/seattle/ca-bc/vancouver/' },
        { from: 'Vancouver', to: 'Seattle', path: '/train/ca-bc/vancouver/us-wa/seattle/' },
    ],
}

/** The "all companies" entry is appended by the builder — it is copy, not a carrier. */
const CARRIERS: Record<Locale, CarrierLink[]> = {
    'en-us': [
        { name: 'Amtrak', path: '/carrier/amtrak-train-146/' },
        { name: 'Acela', path: '/carrier/acela-182/' },
        { name: 'Greyhound Bus', path: '/carrier/greyhound-bus-lines-139/' },
        { name: 'FlixBus USA', path: '/carrier/flixbus-us-1956/' },
        { name: 'Peter Pan Bus', path: '/carrier/peter-pan-bus-lines-132/' },
        { name: 'Red Coach', path: '/carrier/red-coach-148/' },
        { name: 'California Zephyr', path: '/carrier/amtrak-california-zephyr-186/' },
    ],
    'es-mx': [
        { name: 'Tornado Bus', path: '/carrier/tornado-bus-company-74/' },
        { name: 'Tufesa International', path: '/carrier/tufesa-international-123/' },
        { name: 'El Expresso', path: '/carrier/el-expreso-bus-235/' },
        { name: 'AVE Senda', path: '/carrier/ave-senda-169/' },
        { name: 'Transportes del Norte', path: '/carrier/transportes-del-norte-tdn-164/' },
        { name: 'Greyhound Bus', path: '/carrier/greyhound-bus-lines-139/' },
        { name: 'Autobuses Torres', path: '/carrier/autobuses-torres-1266/' },
    ],
}

const CARRIER_INDEX_PATH = '/carrier/'

/**
 * The blog is a single shared surface — it carries no locale prefix. Trailing slash
 * required: `trailingSlash: true` (next.config) 308s `/blog`, and this is both the
 * menu's Blog entry and the blog masthead's brand link on every blog page.
 */
export const blogUrl = '/blog/'

/** Prefix a site path with the active locale: `/carrier/` → `/es-mx/carrier/`. */
function localePath(locale: Locale, path: string): string {
    return `/${locale}${path}`
}

/** The logo target: the locale's own home. en-us keeps the bare root it has always used. */
export function buildSiteLogo(locale: Locale): NavbarLogo {
    return {
        url: locale === 'en-us' ? '/' : `/${locale}/`,
        src: '/next-static/logos/chiku-and-wanderu-horizontal.svg',
        width: 192,
        height: 44,
        alt: 'logo',
    }
}

function routeItems(locale: Locale, routes: RouteLink[], templateKey: string, t: Translate) {
    return routes.map((route) => ({
        title: t(templateKey, { from: route.from, to: route.to }),
        url: localePath(locale, route.path),
    }))
}

export function buildSiteMenu(locale: Locale, t: Translate): MenuItem[] {
    return [
        {
            title: t('buses'),
            url: localePath(locale, '/bus-tickets/'),
            items: routeItems(locale, BUS_ROUTES[locale], 'busRoute', t),
        },
        {
            title: t('trains'),
            url: localePath(locale, '/train-tickets/'),
            items: routeItems(locale, TRAIN_ROUTES[locale], 'trainRoute', t),
        },
        {
            title: t('carriers'),
            shortTitle: t('carriersShort'),
            url: localePath(locale, CARRIER_INDEX_PATH),
            items: [
                ...CARRIERS[locale].map((carrier) => ({
                    title: carrier.name,
                    url: localePath(locale, carrier.path),
                })),
                { title: t('allCarriers'), url: localePath(locale, CARRIER_INDEX_PATH) },
            ],
        },
        // Load-bearing: this must stay `/<locale>/explore/`, not the `/find-tickets/` it
        // sometimes redirects to. Explore is a live experiment route — the router serves
        // control in place and 302s only the treatment bucket at the target — so linking
        // the target forces every visitor into the treatment page and contaminates the
        // experiment. It was "fixed" to the target once (#643, reading the 302 as a
        // permanent edge redirect) and reversed. FR-13a covers it.
        { title: t('explore'), url: localePath(locale, '/explore/') },
        { title: t('blog'), url: blogUrl },
    ]
}

export function buildSiteAuth(locale: Locale, t: Translate): NavbarAuth {
    return {
        login: { title: t('login'), url: localePath(locale, '/account/login/') },
        signup: { title: t('signup'), url: localePath(locale, '/account/signup/') },
        myTrips: { title: t('myTrips'), url: localePath(locale, '/account/my-trips/') },
        account: { title: t('account'), url: localePath(locale, '/account/settings/') },
        points: { title: t('points'), url: localePath(locale, '/account/points/') },
    }
}
